import React, { useState, useContext, useEffect, useMemo } from "react";
import { orderBy } from "lodash";
import { useParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import cx from "classnames";
import {
  IContextVacancies,
  ContextVacancies,
  IErrorInstructorBundle,
} from "./contextVacancies/ContextVacancies";
import Footer from "./Footer";
import Links from "./Links/Links";
import { useBundleEditAbilities } from "./hooks/useBundleEditAbilities";
import {
  Bundle,
  InstructorResponsibilityInput,
  InstructorResponsibilityChangesetInput,
  CubeMutation,
  InstructorLinkValidationErrorUnion,
} from "@models/ISchema";
import InstructorBundle from "@models/Errors/InstructorBundle";
import { IParams } from "@models/IParams";
import responsabilityMutation from "./graphql/responsibilityCube.mutation.graphql";
import updateInstructorLinkTagsMutation from "./graphql/updateInstructorLinkTags.mutation.graphql";
import { CardNotification, Text, Loading, toast } from "@foris/avocado-suite";
import { InstructorLinkTagsError, useVacanciesStore } from "./contextVacancies/useVacanciesStore";
import { useTranslation } from "react-i18next";
import { useUpdateInstructorAssignationType } from "@services/collections/instructor/instructorServices";
import css from "./vacancies.module.scss";
import "react-tabs/style/react-tabs.css";
import { emitEvent } from "@common/hooks/useListenEvent";
import {
  setInstructorRemovalFlowState,
  useInstructorRemovalFlowStore,
} from "@context/instructorRemovalFlow/useInstructorRemovalFlowStore";
import InstructorCorrectionNotice from "./InstructorCorrectionNotice";
import { buildInstructorResponsibilityChangeset } from "./Instructor/utils/buildInstructorResponsibilityChangeset";

export enum VacanciesType {
  instructor = "instructor",
  vacancies = "vacancies",
}

interface IVacanciesGroup {
  active: boolean;
  setActive: (value: boolean) => void;
  dataBundle: Bundle;
  onBlockedExit?: () => void;
}

/** Minimal shape the error CardNotification needs from every error source. */
type RenderableLinkError = { error: { message: string; messageAccent?: string } };

const VacanciesGroup: React.FC<IVacanciesGroup> = (props: IVacanciesGroup) => {
  const { active, setActive, dataBundle, onBlockedExit } = props;
  const { t } = useTranslation();
  const { workspace, origin, scenario, id: bundleId }: IParams = useParams();
  const assignationTypesErrors = useVacanciesStore(state => state.assignationTypesErrors);
  const assignationTypesToSave = useVacanciesStore(state => state.assignationTypesToSave);
  const assignationHaveChanges = useVacanciesStore(state => state.assignationHaveChanges);
  const [instructorMutation] = useMutation(responsabilityMutation, {});
  const [instructorLinkTagsMutation] = useMutation(updateInstructorLinkTagsMutation, {});
  const instructorLinkTagsToSave = useVacanciesStore(state => state.instructorLinkTagsToSave);
  const instructorLinkTagsErrors = useVacanciesStore(state => state.instructorLinkTagsErrors);
  const [loading, setLoading] = useState(false);
  const context: IContextVacancies = useContext(ContextVacancies);
  const { canEnterInstructorEdit } = useBundleEditAbilities(dataBundle);
  const removalStatus = useInstructorRemovalFlowStore(state => state.status);
  const removalContext = useInstructorRemovalFlowStore(state => state.context);
  const removalReasons = useInstructorRemovalFlowStore(state => state.reasons);
  const correctionValidityByLink = useVacanciesStore(
    state => state.instructorCorrectionValidityByLink,
  );
  const isCorrectionMode =
    ["correcting-bundle", "saving-correction"].includes(removalStatus) &&
    removalContext?.workspaceId === String(workspace) &&
    removalContext?.scenarioId === String(scenario) &&
    removalContext?.originId === String(origin) &&
    String(removalContext?.bundleId) === String(bundleId);
  const targetLinkId = isCorrectionMode ? removalContext?.linkId : undefined;
  const targetLeague = context.currentLeague?.find(item => item.linkId === targetLinkId);
  const targetValidity = targetLinkId ? correctionValidityByLink[targetLinkId] : undefined;
  const [validationArray, setValidationArray] =
    useState<InstructorLinkValidationErrorUnion[]>(null);
  const [tagBackendErrors, setTagBackendErrors] = useState<InstructorLinkTagsError[]>([]);
  const [errors, setErrors] = useState(null);
  const warningArray = [];

  const { updateInstructorAssignationType, isLoading: isUpdatingAssignationType } =
    useUpdateInstructorAssignationType();

  const callMutationHook = async (): Promise<boolean> => {
    try {
      const arrayInstructorMutation: InstructorResponsibilityChangesetInput[] =
        buildInstructorResponsibilityChangeset(
          context.currentLeague,
          context.instructorValues,
          targetLinkId,
        );

      const input: InstructorResponsibilityInput = {
        dryRun: false,
        skipValidations: false,
        changeset: arrayInstructorMutation,
      };
      const variables = {
        scenarioId: parseInt(scenario),
        originId: parseInt(origin),
        input,
      };
      const { data } = await instructorMutation({ variables });
      if (data.cube.instructorResponsibilityUpsert.commited) {
        setValidationArray(null);
        context.setErrorsInstructors(null);
        return true;
      }

      console.error("Error InstructorResponsibility");
      const error: CubeMutation = data.cube;
      const validations: InstructorLinkValidationErrorUnion[] =
        error.instructorResponsibilityUpsert.validations;
      setValidationArray(validations);
      return false;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  // Resolves the league a tag validation belongs to. `meta` is pending backend
  // confirmation, so we match it against both linkId and instructorLinkId.
  const resolveLinkFromMeta = (meta?: string) => {
    if (!meta) return null;

    return (
      context.currentLeague?.find(link => link.linkId === meta) ??
      context.currentLeague?.find(link => link.instructors?.some(inst => inst?.id === meta)) ??
      null
    );
  };

  const callInstructorLinkTagsMutation = async (): Promise<boolean> => {
    try {
      const changesets = instructorLinkTagsToSave
        .filter(item => !isCorrectionMode || item.linkId === targetLinkId)
        .map(item => ({
          instructorLinkId: item.instructorLinkId,
          tags: item.tags,
        }));

      const variables = {
        scenarioId: parseInt(scenario),
        originId: parseInt(origin),
        input: { changesets },
      };

      const { data } = await instructorLinkTagsMutation({ variables });
      const payload = data?.cube?.updateInstructorLinkTags;

      if (payload?.commited) return true;

      // Tag validations live in their own channel (mapped to a link), not in
      // `validationArray` (which assumes `meta` is a linkId and would crash the
      // tree when it can't resolve one).
      if (payload?.validations?.length) {
        const tagErrors = payload.validations
          .map(item => ({
            link: resolveLinkFromMeta(item?.meta),
            error: new InstructorBundle(item).getMessage(),
          }))
          .filter((entry): entry is InstructorLinkTagsError => Boolean(entry.link));

        setTagBackendErrors(tagErrors);
      }
      return false;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  const handleUpdateAssignationType = async (): Promise<boolean> => {
    try {
      const changesets = assignationTypesToSave
        ?.filter(assignation => !isCorrectionMode || assignation.linkId === targetLinkId)
        .map(assignation => ({
          instructorLinkId: assignation?.instructorLinkId,
          assignationTypeId: assignation?.data?.id || null,
          linkId: assignation?.linkId,
        }));
      const payload = {
        scenarioId: parseInt(scenario),
        originId: parseInt(origin),
        changesets,
      };

      return await updateInstructorAssignationType(payload);
    } catch (error) {
      console.error("Error in handleUpdateAssignationType:", error);
      return false;
    }
  };

  const handleSubmit = async () => {
    if (loading || isUpdatingAssignationType) return;

    const hasAssignationChanges = isCorrectionMode
      ? assignationTypesToSave.some(item => item.linkId === targetLinkId)
      : assignationHaveChanges;
    const hasResponsibilityChanges = isCorrectionMode
      ? context.instructorValues?.some(values =>
          values?.some(instructor => instructor.linkId === targetLinkId),
        )
      : !!context.instructorValues?.length;
    const hasTagChanges = isCorrectionMode
      ? instructorLinkTagsToSave.some(item => item.linkId === targetLinkId)
      : !!instructorLinkTagsToSave.length;

    if (!hasAssignationChanges && !hasResponsibilityChanges && !hasTagChanges) return;

    setLoading(true);
    setTagBackendErrors([]);

    // Responsibility first (it carries the group-drop cascade), then the tags.
    let committed = true;
    if (hasAssignationChanges) {
      committed = await handleUpdateAssignationType();
    }
    if (hasResponsibilityChanges) {
      committed = committed && (await callMutationHook());
    }
    if (committed && hasTagChanges) {
      committed = await callInstructorLinkTagsMutation();
    }

    setLoading(false);

    // Treat the save as successful only when every dispatched mutation committed.
    if (committed) {
      if (isCorrectionMode) {
        setInstructorRemovalFlowState({ status: "saving-correction" });
        emitEvent("on:refresh", { target: "bundle-view" });
      } else if (hasResponsibilityChanges || hasTagChanges) {
        window.location.reload();
      } else {
        setActive(false);
        toast.success(t("bundle.notifications.success-changes"), { autoClose: 3000 });
        emitEvent("on:refresh", { target: "bundle-view" });
      }
    }
  };

  useEffect(() => {
    const validations: IErrorInstructorBundle[] = [];
    if (validationArray && validationArray.length > 0) {
      validationArray.forEach(item => {
        const validation = new InstructorBundle(item);
        const message = validation.getMessage();
        const findLink = context.currentLeague.find(link => link.linkId === item.meta);
        validations.push({ error: message, link: findLink, item });
        context.setErrorsInstructors(validations);
      });
    } else {
      context.setErrorsInstructors([]);
    }
    const instructorErrors = validations.reduce((acc, curr) => {
      const key = curr.link?.linkId;
      if (!key) return acc;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(curr);
      return acc;
    }, {});

    setErrors(instructorErrors);
  }, [validationArray]);

  const orderList = useMemo(() => {
    const errorsByLinkId: Record<string, { name: string; value: RenderableLinkError[] }> = {};

    const pushError = (linkId?: string, codeName?: string, error?: RenderableLinkError) => {
      if (!linkId || !error) return;
      (errorsByLinkId[linkId] ??= { name: codeName ?? "", value: [] }).value.push(error);
    };

    // Responsibility validations (backend) come pre-grouped by linkId.
    if (errors && validationArray?.length) {
      Object.values(errors as Record<string, IErrorInstructorBundle[]>).forEach(list =>
        list.forEach(item => pushError(item.link?.linkId, item.link?.codeName, item)),
      );
    }

    assignationTypesErrors?.forEach(item =>
      pushError(item.link?.linkId, item.link?.codeName, item),
    );
    instructorLinkTagsErrors?.forEach(item =>
      pushError(item.link?.linkId, item.link?.codeName, item),
    );
    tagBackendErrors?.forEach(item => pushError(item.link?.linkId, item.link?.codeName, item));

    const values = Object.values(errorsByLinkId);

    return values.length ? orderBy(values, "name") : null;
  }, [errors, validationArray, assignationTypesErrors, instructorLinkTagsErrors, tagBackendErrors]);

  return (
    <>
      {(loading || isUpdatingAssignationType) && <Loading className={css.loader} />}
      {isCorrectionMode && (
        <InstructorCorrectionNotice
          leagueCode={targetLeague?.codeName ?? targetLeague?.code}
          reasons={removalReasons}
          validity={targetValidity}
        />
      )}
      <section className={cx(css.content, "container-row")}>
        <Links
          nameGroup={`${dataBundle.code} ${dataBundle.name}`}
          bundleId={dataBundle?.id}
          type={VacanciesType.instructor}
          data={context.currentLeague}
          activeEdit={active}
          editingLinkId={targetLinkId}
        />
      </section>

      {/* validations */}
      {orderList?.length
        ? orderList.map((value: { name: string; value: RenderableLinkError[] }, index: number) => {
            return (
              <CardNotification
                key={index}
                title={`${t(
                  value?.value?.length === 1
                    ? "bundle.link-card.errors.link-error.single"
                    : "bundle.link-card.errors.link-error.plural",
                  {
                    count: value?.value?.length,
                  },
                )} ${value.name}`}
                state="error"
                outlined
                className={css.notificationCard}
              >
                <div className={css.notificationCard_content}>
                  <ul className={css.notificationCard_list}>
                    {value?.value?.map((error: RenderableLinkError, index: number) => (
                      <li key={index} className={css.notificationCard_item}>
                        <Text type="sm">
                          {error?.error?.messageAccent && (
                            <span className={css.notificationCard_messageAccent}>
                              {error.error.messageAccent}{" "}
                            </span>
                          )}
                          {error.error.message}
                        </Text>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardNotification>
            );
          })
        : null}

      <Footer
        activeEdit={active}
        setActiveEdit={setActive}
        canEnterEdit={canEnterInstructorEdit}
        warningsCount={warningArray.length}
        onSave={() => {
          handleSubmit();
        }}
        isSaveValid={isCorrectionMode ? targetValidity?.valid === true : undefined}
        onBlockedExit={onBlockedExit}
      />
    </>
  );
};

export default VacanciesGroup;
